import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import "../styles/SurveyViewer.css";
import FullscreenLoader from "./FullscreenLoader";
import { fetchEsignPortalGrant } from "../helpers/apiHelper";
import { clearSurveyActive, markSurveyActive } from "../helpers/surveyActivity";
import { getModuleContext, getTrustedEsignOrigin } from "../modules/moduleUtils";

// Portal side of the eSign `pg-esign.*` postMessage protocol (v1). The signing page asks for a
// single-use grant; the portal obtains it from its own backend with the client's Cognito access
// token, so the portal login replaces eSign's CPF + e-mail code steps.
export const ESIGN_MESSAGE_VERSION = 1;
export const ESIGN_MESSAGE = {
  requestGrant: "pg-esign.request-grant",
  grant: "pg-esign.grant",
  error: "pg-esign.error",
  completed: "pg-esign.completed",
  close: "pg-esign.close",
};

const readAccessToken = () =>
  localStorage.getItem("accessToken") || sessionStorage.getItem("accessToken") || "";

export default function PortalEsignViewer({
  module,
  onClose,
  closing = false,
  requestGrant = fetchEsignPortalGrant,
}) {
  const iframeRef = useRef(null);
  const pendingRequestsRef = useRef(new Set());
  const completedStepRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [showClose, setShowClose] = useState(false);
  const context = getModuleContext(module);
  const envelopeId = typeof context?.envelopeId === "string" ? context.envelopeId : "";
  const matterId = typeof context?.matterId === "string" ? context.matterId : "";
  const link = typeof context?.link === "string" ? context.link : "";
  const esignOrigin = useMemo(() => getTrustedEsignOrigin(link), [link]);
  const signingUrl = useMemo(() => {
    if (!esignOrigin || !envelopeId || !matterId) return "";
    const separator = link.includes("?") ? "&" : "?";
    return `${link}${separator}embedded=portal&lang=pt`;
  }, [esignOrigin, envelopeId, matterId, link]);

  useEffect(() => {
    const timer = setTimeout(() => setShowClose(true), 1000);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    markSurveyActive();
    return () => {
      clearSurveyActive();
    };
  }, []);

  // The parent hands us a new onClose on every render. Keep the latest props in refs so the
  // message listener below is not torn down mid-grant, which would silently drop the reply.
  const onCloseRef = useRef(onClose);
  const closingRef = useRef(closing);
  const requestGrantRef = useRef(requestGrant);
  useLayoutEffect(() => {
    onCloseRef.current = onClose;
    closingRef.current = closing;
    requestGrantRef.current = requestGrant;
  });

  const handleClose = useCallback(() => {
    if (closingRef.current || typeof onCloseRef.current !== "function") return;
    onCloseRef.current({ completedStep: completedStepRef.current });
  }, []);

  useEffect(() => {
    if (!signingUrl) return undefined;
    let active = true;

    const reply = (payload) => {
      const frameWindow = iframeRef.current?.contentWindow;
      if (!active || !frameWindow) return;
      frameWindow.postMessage(
        { ...payload, version: ESIGN_MESSAGE_VERSION, envelopeId },
        esignOrigin,
      );
    };

    const answerGrantRequest = async (correlationId) => {
      if (pendingRequestsRef.current.has(correlationId)) return;
      pendingRequestsRef.current.add(correlationId);
      try {
        const result = await requestGrantRef.current(readAccessToken(), envelopeId, matterId);
        if (result?.ok) {
          reply({
            type: ESIGN_MESSAGE.grant,
            correlationId,
            grant: result.grant,
            expiresAt: result.expiresAt || null,
          });
        } else {
          reply({
            type: ESIGN_MESSAGE.error,
            correlationId,
            code: result?.code || "PORTAL_CHECK_UNAVAILABLE",
          });
        }
      } catch {
        reply({ type: ESIGN_MESSAGE.error, correlationId, code: "PORTAL_CHECK_UNAVAILABLE" });
      } finally {
        pendingRequestsRef.current.delete(correlationId);
      }
    };

    const handleMessage = (event) => {
      if (event.origin !== esignOrigin) return;
      if (!iframeRef.current || event.source !== iframeRef.current.contentWindow) return;
      const data = event.data;
      if (!data || typeof data !== "object") return;
      if (data.version !== ESIGN_MESSAGE_VERSION || data.envelopeId !== envelopeId) return;

      if (data.type === ESIGN_MESSAGE.requestGrant) {
        if (typeof data.correlationId === "string" && data.correlationId) {
          answerGrantRequest(data.correlationId);
        }
        return;
      }
      if (data.type === ESIGN_MESSAGE.completed) {
        completedStepRef.current = typeof data.step === "string" ? data.step : "COMPLETED";
        return;
      }
      if (data.type === ESIGN_MESSAGE.close) {
        handleClose();
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      active = false;
      window.removeEventListener("message", handleMessage);
    };
  }, [signingUrl, esignOrigin, envelopeId, matterId, handleClose]);

  return (
    <div className="surveyFullscreen">
      <button
        type="button"
        className={`surveyCloseTab ${showClose ? "visible" : ""}`}
        onClick={handleClose}
        disabled={closing}
      >
        {closing ? "Fechando..." : "Fechar"}
      </button>
      {closing ? (
        <FullscreenLoader
          title="Atualizando seu documento"
          message="Estamos verificando o status mais recente."
        />
      ) : signingUrl ? (
        <>
          {!loaded && (
            <FullscreenLoader
              title="Aguarde enquanto preparamos seu documento"
              message="Isso leva apenas alguns segundos."
            />
          )}
          <iframe
            ref={iframeRef}
            src={signingUrl}
            title="Assinatura de documento"
            className={`surveyFullscreenFrame ${loaded ? "loaded" : ""}`}
            onLoad={() => setLoaded(true)}
          />
        </>
      ) : (
        <div className="surveyEmptyState">Este documento não está disponível.</div>
      )}
    </div>
  );
}
